/**
 * ESCHER FALLS — ピース種別ごとのメッシュ生成。MODULE-CONTRACTS §7。
 *
 * 方針:
 * - 色・マテリアルは一切ここで決めない。すべて `./materials` の共有シングルトンを使う。
 * - **輪郭線が生命線**（CORE-SPEC §6）。面の稜線だけでなく、石積みの目地・迫石の割付といった
 *   「面の上に乗る線」も同じインクで引く。版画は線で描くものなので、階調より線を優先する。
 * - 輪郭線は `LineSegments2`（太さを CSS px で指定できる）で引き、
 *   **1つの transform 単位につき 1 本のバッファにまとめる**（＝ドローコール1回）。
 *   まとめる単位は「ピースの Group」と「回転する車輪の Group」と「伸縮する水チューブの各部材」。
 * - ジオメトリはキー付きキャッシュで共有する。`clearMeshRegistry()` で全て dispose する。
 * - 生成物の親 Group は原点のまま。ワールド座標は子の transform が持つ。
 */
import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import type {
  LeverDef,
  TurntableDef,
  Axis6,
  CornerDef,
  FallDef,
  GoalDef,
  Piece,
  PieceId,
  PillarDef,
  Port,
  PortId,
  Segment,
  SegmentId,
  SourceDef,
  StraightDef,
  Vec3,
  WaterwheelDef,
} from '../core/types';
import { getMaterials } from './materials';
import type { MaterialSet } from './materials';

// =============================================================================
// 寸法（MODULE-CONTRACTS §7.2 が固定するもの ＋ そこから導いた内訳）
// =============================================================================

/** パイプ外形の断面（1マスあたり 0.7 × 0.7）。 */
const PIPE_OUTER = 0.7;
/** 樋の側壁の厚み。 */
const PIPE_WALL = 0.1;
/** 樋の床の厚み。 */
const TROUGH_FLOOR = 0.12;
/** 水チューブの断面。 */
const WATER_W = 0.44;
/** `fall` の水チューブの断面（外形を作らないぶん細い）。 */
const FALL_WATER_W = 0.34;
/** 水車の半径。 */
const WHEEL_R = 0.45;
/** 柱の断面。 */
const PILLAR_W = 0.9;

const HALF = PIPE_OUTER / 2; // 0.35
/** 側壁の中心オフセット（外面が ±HALF に来る）。 */
const WALL_OFF = HALF - PIPE_WALL / 2; // 0.30
/** 床の中心オフセット。 */
const FLOOR_OFF = -HALF + TROUGH_FLOOR / 2; // -0.29
/** 側壁（縁を載せるぶん低い）の高さと中心。 */
const RIM_H = 0.08;
const SIDE_H = PIPE_OUTER - RIM_H; // 0.62
const SIDE_OFF = -HALF + SIDE_H / 2; // -0.04
const RIM_OFF = HALF - RIM_H / 2; // 0.31
/** 縁の幅と中心オフセット（外面は側壁と揃え、内側へ少しだけ張り出す）。 */
const RIM_W = 0.12;
const RIM_LAT = HALF - RIM_W / 2; // 0.29
/** 側壁の内法。床はこの幅にして側壁と同一平面を作らない。 */
const INNER_W = PIPE_OUTER - PIPE_WALL * 2; // 0.5

/** 目地線を面からわずかに浮かせる量（深度の引き分けを避ける保険）。 */
const INK_LIFT = 0.0015;
/** 石積みの目地の割付ピッチ（ワールド単位）。1マスに2段。 */
const COURSE = 0.5;

// =============================================================================
// 基本ベクトル
// =============================================================================

const UP = new THREE.Vector3(0, 1, 0);
const FORWARD = new THREE.Vector3(0, 0, 1);
const IDENTITY_Q = new THREE.Quaternion();

const AXIS_TABLE: Readonly<Record<Axis6, THREE.Vector3>> = {
  '+x': new THREE.Vector3(1, 0, 0),
  '-x': new THREE.Vector3(-1, 0, 0),
  '+y': new THREE.Vector3(0, 1, 0),
  '-y': new THREE.Vector3(0, -1, 0),
  '+z': new THREE.Vector3(0, 0, 1),
  '-z': new THREE.Vector3(0, 0, -1),
};

/** 水平な4方向。`corner` の壁の有無をこの順で判定する。 */
const HORIZONTAL_AXES: readonly Axis6[] = ['+x', '-x', '+z', '-z'];

function axisVec(a: Axis6): THREE.Vector3 {
  return AXIS_TABLE[a].clone();
}

function isVertical(a: Axis6): boolean {
  return a === '+y' || a === '-y';
}

function toVector3(v: Vec3): THREE.Vector3 {
  return new THREE.Vector3(v[0], v[1], v[2]);
}

function mats(): MaterialSet {
  return getMaterials();
}

// =============================================================================
// レジストリ（MODULE-CONTRACTS §7.1：モジュールレベルの副作用付き）
// =============================================================================

const waterRegistry = new Map<SegmentId, WaterTube>();
const wheelRegistry = new Map<PieceId, THREE.Object3D>();
const anchorRegistry = new Map<PortId, THREE.Vector3>();
const geometryCache = new Map<string, THREE.BufferGeometry>();
/** 稜線の頂点配列のキャッシュ（GPU 資源ではないので dispose 不要）。 */
const edgeCache = new Map<string, Float32Array>();
/** 水チューブの輪郭線ジオメトリ（断面ごとに1つ共有する）。 */
const waterEdgeCache = new Map<number, LineSegmentsGeometry>();
/** ピースごとに1本作る輪郭線ジオメトリ。共有できないので個別に dispose する。 */
const inkGeometries: LineSegmentsGeometry[] = [];

/** キャッシュ済みジオメトリ。`key` は稜線キャッシュのキーにも使う。 */
interface Geo {
  readonly key: string;
  readonly geom: THREE.BufferGeometry;
}

function cached(key: string, create: () => THREE.BufferGeometry): Geo {
  let geom = geometryCache.get(key);
  if (geom === undefined) {
    geom = create();
    geometryCache.set(key, geom);
  }
  return { key, geom };
}

function boxGeo(sx: number, sy: number, sz: number): Geo {
  return cached(`box:${sx}:${sy}:${sz}`, () => new THREE.BoxGeometry(sx, sy, sz));
}

function cylinderGeo(r: number, h: number, seg: number, thetaStart = 0): Geo {
  return cached(
    `cyl:${r}:${h}:${seg}:${thetaStart}`,
    () => new THREE.CylinderGeometry(r, r, h, seg, 1, false, thetaStart),
  );
}

/** 正八角柱。`apothem` は「面の中心までの距離」。八角形の平らな面が軸に正対する。 */
function octaGeo(apothem: number, h: number): Geo {
  return cylinderGeo(apothem / Math.cos(Math.PI / 8), h, 8, Math.PI / 8);
}

/** 原点が下端（+Y 側へ伸びる）の単位長チューブ。scale.y で伸ばす。 */
function waterGeo(w: number): Geo {
  return cached(`water:${w}`, () => {
    const g = new THREE.BoxGeometry(w, 1, w);
    // 入口側を pivot にするため、中心ではなく下端を原点に移す。
    g.translate(0, 0.5, 0);
    return g;
  });
}

/** ジオメトリの稜線を「線分の端点列」として取り出す（キャッシュ）。 */
function edgeArray(g: Geo): Float32Array {
  let arr = edgeCache.get(g.key);
  if (arr === undefined) {
    const edges = new THREE.EdgesGeometry(g.geom);
    arr = Float32Array.from(edges.attributes.position.array);
    edges.dispose(); // 太線化するので EdgesGeometry 自体は保持しない
    edgeCache.set(g.key, arr);
  }
  return arr;
}

/** `waterGeo` と同じ形の、太線用の稜線ジオメトリ（水チューブは伸縮するので共有インスタンスを子に持つ）。 */
function waterEdgeGeo(w: number): LineSegmentsGeometry {
  const hit = waterEdgeCache.get(w);
  if (hit !== undefined) return hit;
  const geo = new LineSegmentsGeometry();
  geo.setPositions(edgeArray(waterGeo(w)));
  waterEdgeCache.set(w, geo);
  return geo;
}

// =============================================================================
// 輪郭線（インク）
// =============================================================================

const TMP_SIZE = new THREE.Vector2();
const TMP_V = new THREE.Vector3();

/**
 * `LineMaterial` は `resolution` を基準に太さを決める。
 * **CSS サイズ**を入れることで `linewidth` が CSS ピクセルになり、DPR に依存しなくなる
 * （`LineSegments2` 既定の `onBeforeRender` はドローイングバッファ＝物理ピクセルを入れるので
 * Retina で線が半分の太さになってしまう。ここで上書きする）。
 */
function syncInkResolution(renderer: THREE.WebGLRenderer): void {
  renderer.getSize(TMP_SIZE);
  mats().outlineInk.resolution.copy(TMP_SIZE);
}

function makeInkLine(geo: LineSegmentsGeometry): LineSegments2 {
  const line = new LineSegments2(geo, mats().outlineInk);
  line.onBeforeRender = syncInkResolution;
  return line;
}

/**
 * 組み立て中の1 transform 単位。
 * `ink` に貯めた線分は最後に `flushInk()` で1本の `LineSegments2` にまとめる。
 */
interface Build {
  readonly root: THREE.Object3D;
  /** 線分の端点列（6要素で1本）。root のローカル座標。 */
  readonly ink: number[];
}

function newBuild(root: THREE.Object3D): Build {
  return { root, ink: [] };
}

function flushInk(b: Build): void {
  if (b.ink.length === 0) return;
  const geo = new LineSegmentsGeometry();
  geo.setPositions(new Float32Array(b.ink));
  inkGeometries.push(geo);
  b.root.add(makeInkLine(geo));
}

function pushSeg(b: Build, a: THREE.Vector3, c: THREE.Vector3): void {
  b.ink.push(a.x, a.y, a.z, c.x, c.y, c.z);
}

/** ジオメトリの稜線を、その配置のまま ink バッファへ写す。 */
function pushEdges(b: Build, g: Geo, pos: THREE.Vector3, quat: THREE.Quaternion): void {
  const arr = edgeArray(g);
  for (let i = 0; i < arr.length; i += 3) {
    TMP_V.set(arr[i], arr[i + 1], arr[i + 2]).applyQuaternion(quat).add(pos);
    b.ink.push(TMP_V.x, TMP_V.y, TMP_V.z);
  }
}

// =============================================================================
// 低レベルの組み立てヘルパ
// =============================================================================

function addGeo(
  b: Build,
  g: Geo,
  material: THREE.Material,
  pos: THREE.Vector3,
  quat: THREE.Quaternion = IDENTITY_Q,
): void {
  const mesh = new THREE.Mesh(g.geom, material);
  mesh.position.copy(pos);
  mesh.quaternion.copy(quat);
  b.root.add(mesh);
  pushEdges(b, g, pos, quat);
}

/**
 * ローカル座標系。`origin` を原点、`quat` を回転とする。
 * 走行方向を持つ部材では **ローカル X = 横、Y = 走行方向、Z = 上** に揃えてある。
 */
interface Frame {
  readonly origin: THREE.Vector3;
  readonly quat: THREE.Quaternion;
}

/** ワールド軸そのままのフレーム（ローカル = (x, y, z)、Y が上）。 */
function worldFrame(origin: THREE.Vector3): Frame {
  return { origin, quat: IDENTITY_Q };
}

/**
 * 走行方向 `dir` に沿ったフレームを作る。
 * ローカル Y が `dir`、ローカル Z が「上」（`dir` が垂直なときは +Z）になる。
 */
function runFrame(origin: THREE.Vector3, dir: THREE.Vector3): Frame {
  const y = dir.clone().normalize();
  const ref = Math.abs(y.y) > 0.9 ? FORWARD : UP;
  const z = ref.clone().addScaledVector(y, -ref.dot(y)).normalize();
  const x = new THREE.Vector3().crossVectors(y, z);
  const quat = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(x, y, z),
  );
  return { origin, quat };
}

function framePoint(f: Frame, lx: number, ly: number, lz: number): THREE.Vector3 {
  return new THREE.Vector3(lx, ly, lz).applyQuaternion(f.quat).add(f.origin);
}

function addFramedBox(
  b: Build,
  f: Frame,
  size: readonly [number, number, number],
  local: readonly [number, number, number],
  material: THREE.Material,
): void {
  const g = boxGeo(size[0], size[1], size[2]);
  addGeo(b, g, material, framePoint(f, local[0], local[1], local[2]), f.quat);
}

/** フレームのローカル座標で目地線を1本引く。 */
function addFramedInk(
  b: Build,
  f: Frame,
  a: readonly [number, number, number],
  c: readonly [number, number, number],
): void {
  pushSeg(b, framePoint(f, a[0], a[1], a[2]), framePoint(f, c[0], c[1], c[2]));
}

// =============================================================================
// 水チューブ
// =============================================================================

/** セグメント1本ぶんの水チューブ。充填率を外から与える。 */
export interface WaterMesh {
  readonly segmentId: SegmentId;
  /** 実体。scene グラフ上は piece の Object3D の子。 */
  readonly mesh: THREE.Mesh;
  /**
   * 充填率を設定する。`0` で完全に空（不可視）、`1` で満水。
   * 水は `Segment.fromPort` 側から `toPort` 側へ伸びる。
   */
  setFill(t: number): void;
  /** true にすると `toPort` 側から `fromPort` 側へ伸びる（逆流）。既定 false。 */
  setReversed(reversed: boolean): void;
  /** クリア演出用にアクセント色へ切り替える。 */
  setAccent(on: boolean): void;
}

/**
 * 折れ線（`corner` なら 3 点、それ以外は 2 点）に沿って伸びる水。
 *
 * 各パートのジオメトリは「原点＝下端、+Y 方向へ長さ1」に translate 済みなので、
 * パートを **流入側の点に置いて scale.y を伸ばす**だけで
 * 「入口から満ちていく」表現になる（中心から伸びない）。
 * 上向き・下向き・水平のいずれでも、向きは quaternion が吸収するので同じ扱いでよい。
 */
class WaterTube implements WaterMesh {
  readonly segmentId: SegmentId;
  readonly mesh: THREE.Mesh;

  private readonly points: readonly THREE.Vector3[];
  private readonly parts: readonly THREE.Mesh[];
  private readonly lengths: number[];
  private reversed = false;
  private fill = 0;

  constructor(
    parent: THREE.Object3D,
    segmentId: SegmentId,
    points: readonly THREE.Vector3[],
    width: number,
  ) {
    this.segmentId = segmentId;
    this.points = points;
    const g = waterGeo(width);
    const edges = waterEdgeGeo(width);
    const parts: THREE.Mesh[] = [];
    for (let i = 0; i + 1 < points.length; i++) {
      const mesh = new THREE.Mesh(g.geom, mats().water);
      // 輪郭線は子にする。親の scale.y に追従して一緒に伸びる。
      mesh.add(makeInkLine(edges));
      parent.add(mesh);
      parts.push(mesh);
    }
    this.parts = parts;
    this.lengths = parts.map(() => 0);
    this.mesh = parts[0];
    this.layout();
    this.setFill(0);
  }

  /** `reversed` に応じて各パートの位置と向きを置き直す。 */
  private layout(): void {
    const pts = this.reversed ? [...this.points].reverse() : [...this.points];
    for (let i = 0; i < this.parts.length; i++) {
      const from = pts[i];
      const to = pts[i + 1];
      const delta = to.clone().sub(from);
      const len = delta.length();
      this.lengths[i] = len;
      const part = this.parts[i];
      part.position.copy(from);
      if (len > 1e-6) {
        part.quaternion.copy(runFrame(from, delta).quat);
      }
    }
  }

  setFill(t: number): void {
    this.fill = Math.min(1, Math.max(0, t));
    let total = 0;
    for (const len of this.lengths) total += len;
    const filled = total * this.fill;
    let acc = 0;
    for (let i = 0; i < this.parts.length; i++) {
      const len = this.lengths[i];
      const f = len > 1e-6 ? Math.min(1, Math.max(0, (filled - acc) / len)) : 0;
      const part = this.parts[i];
      part.scale.set(1, Math.max(len * f, 1e-4), 1);
      part.visible = f > 1e-4;
      acc += len;
    }
  }

  setReversed(reversed: boolean): void {
    if (reversed === this.reversed) return;
    this.reversed = reversed;
    this.layout();
    this.setFill(this.fill);
  }

  setAccent(on: boolean): void {
    const m = on ? mats().waterClear : mats().water;
    for (const part of this.parts) part.material = m;
  }

  /** 二重登録されたときに前のものを捨てる（§7.1）。ジオメトリは共有なので dispose しない。 */
  disposeParts(): void {
    for (const part of this.parts) part.removeFromParent();
  }
}

// =============================================================================
// 部材ヘルパ
// =============================================================================

/**
 * 樋の外側面（ローカル X = ±HALF）に石積みの目地を刻む。
 * 通し目地（横1本）＋ 上下段でずらした縦目地。イギリス積みの割付。
 */
function inkTroughCoursing(b: Build, f: Frame, len: number): void {
  const zBottom = -HALF;
  const zTop = RIM_OFF - RIM_H / 2; // 縁石の下端 ＝ 側壁の上端
  const zMid = (zBottom + zTop) / 2;
  const half = len / 2;
  const eps = 1e-6;
  for (const sign of [1, -1]) {
    const x = sign * (HALF + INK_LIFT);
    addFramedInk(b, f, [x, -half, zMid], [x, half, zMid]);
    for (let y = -half + COURSE; y < half - eps; y += COURSE) {
      addFramedInk(b, f, [x, y, zBottom], [x, y, zMid]);
    }
    for (let y = -half + COURSE / 2; y < half - eps; y += COURSE) {
      addFramedInk(b, f, [x, y, zMid], [x, y, zTop]);
    }
  }
}

/**
 * 走行区間（樋 or 立ち上がりの井筒）を1本置く。
 * - 水平方向: 上面が開いた U 字の樋。側壁・縁石・水切りの3段構成にして石造らしくする。
 * - 垂直方向: 4隅の柱＋マス境界の帯（4面を塞ぐと中の水が見えなくなる。
 *   水はこのゲームで唯一の彩度であり、進行のフィードバックそのもの）。
 */
function addRun(b: Build, from: THREE.Vector3, to: THREE.Vector3, cells: number): void {
  const delta = to.clone().sub(from);
  const len = delta.length();
  if (len < 1e-6) return;
  const center = from.clone().addScaledVector(delta, 0.5);
  const f = runFrame(center, delta);
  const stone = mats().stone;
  const mid = mats().stoneMid;
  const dark = mats().stoneDark;
  const vertical = Math.abs(delta.clone().normalize().y) > 0.9;

  if (vertical) {
    const post = 0.16;
    const postOff = HALF - post / 2; // 0.27
    for (const sx of [1, -1]) {
      for (const sz of [1, -1]) {
        addFramedBox(b, f, [post, len, post], [sx * postOff, 0, sz * postOff], stone);
      }
    }
    // 帯：両端と各マス境界に巻く（版画らしい水平線を作り、継ぎ目も締まる）
    for (let i = 0; i <= cells; i++) {
      const band = i === 0 || i === cells ? 0.12 : 0.09;
      const width = i === 0 || i === cells ? 0.86 : 0.8;
      const edge = i === 0 ? band / 2 : i === cells ? -band / 2 : 0;
      const y = -len / 2 + (len / cells) * i + edge;
      addFramedBox(b, f, [width, band, width], [0, y, 0], mid);
    }
    return;
  }

  // 上面が開いた U 字の樋。
  // 床は側壁の内側に収める（外面を側壁と同一平面にすると、同じ向きの面が重なって
  // z-fighting する。突き合わせは背面が culling されるので安全）。
  addFramedBox(b, f, [INNER_W, len, TROUGH_FLOOR], [0, 0, FLOOR_OFF], stone);
  addFramedBox(b, f, [PIPE_WALL, len, SIDE_H], [WALL_OFF, 0, SIDE_OFF], stone);
  addFramedBox(b, f, [PIPE_WALL, len, SIDE_H], [-WALL_OFF, 0, SIDE_OFF], stone);
  // 縁石（水路の口を縁取ると版画の線が締まる）。内側は 0.23 までなので水 0.44 と干渉しない。
  addFramedBox(b, f, [RIM_W, len, RIM_H], [RIM_LAT, 0, RIM_OFF], mid);
  addFramedBox(b, f, [RIM_W, len, RIM_H], [-RIM_LAT, 0, RIM_OFF], mid);
  // 水切りの繰形（樋の腹に通す1本の帯。石造の橋脚らしさが出る）
  addFramedBox(b, f, [0.86, len, 0.06], [0, 0, -HALF - 0.02], mid);
  // マス境界の下に受け材（持ち送り）を出す。
  for (let i = 1; i < cells; i++) {
    const y = -len / 2 + (len / cells) * i;
    addFramedBox(b, f, [0.3, 0.16, 0.09], [0, y, -0.45], dark);
  }
  inkTroughCoursing(b, f, len);
}

// =============================================================================
// 種別ごとの造形
// =============================================================================

/** 水源：石造りのアーチ状の吐水口。 */
function buildSource(b: Build, def: SourceDef): void {
  const p = toVector3(def.pos);
  const d = axisVec(def.dir);
  const f = runFrame(p, d); // ローカル: X=横, Y=吐出方向, Z=上
  const stone = mats().stone;
  const mid = mats().stoneMid;
  const dark = mats().stoneDark;

  // 奥の石塊（2段積み。継ぎ目が1本入るだけで「積んである」ように見える）
  addFramedBox(b, f, [0.9, 0.3, 0.9], [0, -0.35, 0], dark);
  addFramedBox(b, f, [0.88, 0.22, 0.88], [0, -0.09, 0], dark);
  // 開口の枠（左右の柱・まぐさ・敷居）。ローカル Y = 0〜0.16 の1枚の面をなす。
  addFramedBox(b, f, [0.16, 0.16, 0.72], [0.32, 0.08, 0], stone);
  addFramedBox(b, f, [0.16, 0.16, 0.72], [-0.32, 0.08, 0], stone);
  addFramedBox(b, f, [0.8, 0.16, 0.16], [0, 0.08, 0.32], stone);
  addFramedBox(b, f, [0.8, 0.16, 0.14], [0, 0.08, -0.33], stone);
  // 要石（keystone）。まぐさの中央だけ少し手前へ出す。アーチの頂点にちょうど載る。
  addFramedBox(b, f, [0.14, 0.2, 0.1], [0, 0.1, 0.29], mid);

  // アーチは**線で描く**。石を階段状に積んで暗示するより、迫石（voussoir）の
  // 割付をインクで引くほうが版画としてはるかに読みやすい（積むと小さな箱の塊になる）。
  const fy = 0.17; // 枠の前面 0.16 のすぐ手前
  const arcR = 0.24;
  const steps = 10;
  let prev = new THREE.Vector3(arcR, fy, 0);
  for (let i = 1; i <= steps; i++) {
    const th = (i / steps) * Math.PI;
    const cur = new THREE.Vector3(Math.cos(th) * arcR, fy, Math.sin(th) * arcR);
    addFramedInk(b, f, [prev.x, fy, prev.z], [cur.x, fy, cur.z]);
    prev = cur;
  }
  for (let i = 1; i < 4; i++) {
    const th = (i / 4) * Math.PI;
    addFramedInk(
      b,
      f,
      [Math.cos(th) * arcR, fy, Math.sin(th) * arcR],
      [Math.cos(th) * 0.37, fy, Math.sin(th) * 0.37],
    );
  }
  // 迫元（springing）の高さに通し目地
  for (const sx of [1, -1]) {
    addFramedInk(b, f, [sx * arcR, fy, 0], [sx * 0.4, fy, 0]);
  }

  // 吐水口の樋（ポート位置 = 走行方向 0.5 まで）。断面は本線の樋と揃える。
  const sp = 0.33; // 走行方向の中心（0.16〜0.50）
  addFramedBox(b, f, [INNER_W, 0.34, TROUGH_FLOOR], [0, sp, FLOOR_OFF], stone);
  addFramedBox(b, f, [PIPE_WALL, 0.34, 0.3], [WALL_OFF, sp, -0.2], stone);
  addFramedBox(b, f, [PIPE_WALL, 0.34, 0.3], [-WALL_OFF, sp, -0.2], stone);
  addFramedBox(b, f, [RIM_W, 0.34, RIM_H], [RIM_LAT, sp, -0.01], mid);
  addFramedBox(b, f, [RIM_W, 0.34, RIM_H], [-RIM_LAT, sp, -0.01], mid);
  // 水切り（吐水口の下に回す繰形）
  addFramedBox(b, f, [0.6, 0.12, 0.06], [0, 0.43, -0.38], mid);
}

/** ゴール：八角形の水盤。基壇・盤身・縁を段で重ね、流入方向の縁だけ切り欠く。 */
/**
 * 水受け。`dry` を立てると涸れ井戸になる。
 *
 * 形を変えず**明度だけ落とす**。別の形にすると「水が溜まる場所」に見えなくなり、
 * 入ってはいけない受け口であることが伝わらない。
 */
function buildGoal(b: Build, def: GoalDef, dry = false): void {
  const p = toVector3(def.pos);
  const d = axisVec(def.dir);
  // 涸れ井戸は一段暗く沈める。彩度は足さない（水だけが彩度を持つ原則のまま）。
  const stone = dry ? mats().stoneDark : mats().stone;
  const mid = dry ? mats().stoneDark : mats().stoneMid;
  const dark = mats().stoneDark;

  // 台座 → 基壇 → 盤身（この上面 -0.16 が水の溜まる底になる）
  addGeo(b, octaGeo(0.36, 0.12), dark, p.clone().add(new THREE.Vector3(0, -0.44, 0)));
  addGeo(b, octaGeo(0.45, 0.08), mid, p.clone().add(new THREE.Vector3(0, -0.34, 0)));
  addGeo(b, octaGeo(0.41, 0.14), stone, p.clone().add(new THREE.Vector3(0, -0.23, 0)));

  // 縁（8面の立ち上がり）。この内側が窪みになる。
  const apothem = 0.4;
  const wallT = 0.09;
  const wallH = 0.22; // 底 -0.16 から縁 +0.06 まで
  // 隣り合う板の**外側の稜が隅でちょうど接する**長さ。
  // これより長いと隅が階段状に飛び出し、板がばらばらの積み木に見える。
  const side = 2 * (apothem + wallT / 2) * Math.tan(Math.PI / 8);

  // 流入方向に一番近い面を開ける（水平方向のときだけ）
  let skip = -1;
  if (!isVertical(def.dir)) {
    let best = -Infinity;
    for (let k = 0; k < 8; k++) {
      const th = (k * Math.PI) / 4;
      const dot = Math.cos(th) * d.x + Math.sin(th) * d.z;
      if (dot > best) {
        best = dot;
        skip = k;
      }
    }
  }

  for (let k = 0; k < 8; k++) {
    if (k === skip) continue;
    const th = (k * Math.PI) / 4;
    // 外向き法線が (cosθ, 0, sinθ) になる回転は Y まわり -θ。
    const quat = new THREE.Quaternion().setFromAxisAngle(UP, -th);
    const pos = new THREE.Vector3(
      Math.cos(th) * apothem,
      -0.16 + wallH / 2,
      Math.sin(th) * apothem,
    ).add(p);
    addGeo(b, boxGeo(wallT, wallH, side), stone, pos, quat);
  }

  // 受け口。八角形の開口（幅 0.37）には樋（幅 0.7）は差し込めないので、
  // 切り欠いた面の**外側**に、流入してくる樋と同じ断面位置で受け石と頬石を置く。
  // 水の底面（-0.22）のすぐ下に受け石の天端（-0.23）が来るので、
  // 水がそのまま石の上を滑って水盤へ落ちるように見える。
  if (!isVertical(def.dir)) {
    const f = runFrame(p, d);
    addFramedBox(b, f, [0.7, 0.16, 0.1], [0, 0.42, -0.28], stone);
    addFramedBox(b, f, [PIPE_WALL, 0.16, 0.34], [WALL_OFF, 0.42, -0.06], stone);
    addFramedBox(b, f, [PIPE_WALL, 0.16, 0.34], [-WALL_OFF, 0.42, -0.06], stone);
  }
}

/** 直線水路。ポート `a` = `p - 0.5d` から `b` = `p + (length-0.5)d` までを1本の樋で覆う。 */
function buildStraight(b: Build, def: StraightDef): void {
  const d = axisVec(def.dir);
  const p = toVector3(def.pos);
  const from = p.clone().addScaledVector(d, -0.5);
  const to = p.clone().addScaledVector(d, def.length - 0.5);
  addRun(b, from, to, def.length);
}

/**
 * L 字の樋。
 *
 * 「セルの4つの水平面のうち、ポートになっていない面にだけ壁を立てる」という
 * 単純な規則で作る。こうすると外側の壁が自然に水を曲げる形になり、
 * かつ壁がもう一方の腕の水路に食い込まない（斜めの部材を置くと必ず食い込む）。
 */
function buildCorner(b: Build, def: CornerDef): void {
  const p = toVector3(def.pos);
  const f = worldFrame(p);
  const stone = mats().stone;
  const mid = mats().stoneMid;
  const isPort = (a: Axis6): boolean => a === def.from || a === def.to;

  // 部材をその方向へどこまで伸ばすか。
  // **ポート面の側はセル面（0.5）まで伸ばす**。0.35（外形の半分）で止めると
  // 隣の straight の樋との間に 0.15 の隙間が空き、水だけが宙に浮いて見える。
  const reach = (a: Axis6): number => (isPort(a) ? 0.5 : HALF);
  /** 直交する壁が立つ側は、その厚みぶん手前で止める（同一平面を作らないため）。 */
  const inset = (a: Axis6): number => (isPort(a) ? 0.5 : HALF - PIPE_WALL);
  const insetRim = (a: Axis6): number => (isPort(a) ? 0.5 : HALF - RIM_W);

  const span = (plus: number, minus: number): [number, number] => [plus + minus, (plus - minus) / 2];

  for (const axis of HORIZONTAL_AXES) {
    if (isPort(axis)) continue;
    const v = axisVec(axis);
    if (v.x !== 0) {
      // X 法線の壁。Z 方向は端まで通す。
      const [len, c] = span(reach('+z'), reach('-z'));
      addFramedBox(b, f, [PIPE_WALL, SIDE_H, len], [v.x * WALL_OFF, SIDE_OFF, c], stone);
      addFramedBox(b, f, [RIM_W, RIM_H, len], [v.x * RIM_LAT, RIM_OFF, c], mid);
      // 外壁の目地（横1本＋縦2本）
      const x = v.x * (HALF + INK_LIFT);
      addFramedInk(b, f, [x, -0.04, c - len / 2], [x, -0.04, c + len / 2]);
      for (const t of [-0.25, 0.25]) {
        addFramedInk(b, f, [x, -HALF, c + t], [x, -0.04, c + t]);
      }
    } else {
      // Z 法線の壁。X 方向は X 壁の手前で止める。
      const [len, c] = span(inset('+x'), inset('-x'));
      addFramedBox(b, f, [len, SIDE_H, PIPE_WALL], [c, SIDE_OFF, v.z * WALL_OFF], stone);
      const [rlen, rc] = span(insetRim('+x'), insetRim('-x'));
      addFramedBox(b, f, [rlen, RIM_H, RIM_W], [rc, RIM_OFF, v.z * RIM_LAT], mid);
      const z = v.z * (HALF + INK_LIFT);
      addFramedInk(b, f, [c - len / 2, -0.04, z], [c + len / 2, -0.04, z]);
      for (const t of [-0.25, 0.25]) {
        addFramedInk(b, f, [c + t, -HALF, z], [c + t, -0.04, z]);
      }
    }
  }

  // 外側の隅に隅石の柱を立てる。折れ点がはっきりし、エッシャーの建築らしくなる。
  for (const ax of ['+x', '-x'] as const) {
    for (const az of ['+z', '-z'] as const) {
      if (isPort(ax) || isPort(az)) continue;
      const vx = axisVec(ax).x;
      const vz = axisVec(az).z;
      addFramedBox(b, f, [0.18, 0.78, 0.18], [vx * 0.35, 0.04, vz * 0.35], stone);
      addFramedBox(b, f, [0.24, 0.07, 0.24], [vx * 0.35, 0.46, vz * 0.35], mid);
    }
  }

  // 下向きのポートがあるときは床を抜く（水が下へ落ちるため）。
  if (!isPort('-y')) {
    const [sx, cx] = span(inset('+x'), inset('-x'));
    const [sz, cz] = span(inset('+z'), inset('-z'));
    addFramedBox(b, f, [sx, TROUGH_FLOOR, sz], [cx, FLOOR_OFF, cz], stone);
  }
}

/**
 * 垂直落下。細い水路が垂直に落ちる井筒。
 *
 * MODULE-CONTRACTS §7.2 は「パイプ外形は作らない」としているが、それだと
 * 空のとき何も見えずレベルが読めない。そこで**セル面の中央**に4枚の支持壁を立て、
 * 四隅を開けたままにする。等角投影ではカメラに最も近い隅（＝開いている側）が
 * 常に水柱の中心に重なるので、どの視点でも落下する水は隠れない。
 */
function buildFall(b: Build, def: FallDef): void {
  const p = toVector3(def.pos);
  const h = def.height;
  const topY = p.y + 0.5;
  const botY = topY - h;
  const stone = mats().stone;
  const mid = mats().stoneMid;

  // 支持壁：厚み 0.10、幅 0.30、外面が 0.49（セル内に収まる）。
  const wallR = 0.44;
  const wallT = 0.1;
  const wallW = 0.3;
  const midY = (topY + botY) / 2;
  addGeo(b, boxGeo(wallT, h, wallW), stone, new THREE.Vector3(p.x + wallR, midY, p.z));
  addGeo(b, boxGeo(wallT, h, wallW), stone, new THREE.Vector3(p.x - wallR, midY, p.z));
  addGeo(b, boxGeo(wallW, h, wallT), stone, new THREE.Vector3(p.x, midY, p.z + wallR));
  addGeo(b, boxGeo(wallW, h, wallT), stone, new THREE.Vector3(p.x, midY, p.z - wallR));

  // 締め輪：マス境界ごとに巻く。落下の速度感を出す水平線でもある。
  // 支持壁の外面（0.49）より内側に収めて、棚のように出っ張らせない。
  // 両端の輪は水柱の端面と同一平面にならないよう内側へずらす（z-fighting 防止）。
  for (let i = 0; i <= h; i++) {
    const end = i === 0 || i === h;
    const ringH = end ? 0.1 : 0.06;
    const edge = i === 0 ? -ringH / 2 - 0.02 : i === h ? ringH / 2 + 0.02 : 0;
    const y = topY - i + edge;
    addGeo(b, boxGeo(0.94, ringH, 0.94), mid, new THREE.Vector3(p.x, y, p.z));
  }
}

/** 水車：通水する樋 ＋ 側面の8枚羽根の車輪 ＋ 軸受け。 */
function buildWaterwheel(b: Build, piece: Piece, def: WaterwheelDef): void {
  const dirAxis: Axis6 = def.dir ?? '+y';
  const d = axisVec(dirAxis);
  const p = toVector3(def.pos);
  addRun(b, p.clone().addScaledVector(d, -0.5), p.clone().addScaledVector(d, 0.5), 1);

  // 車軸は「水平で、かつ通水軸に直交する」向き（MODULE-CONTRACTS §7.2）。
  const spin = isVertical(dirAxis)
    ? new THREE.Vector3(1, 0, 0)
    : Math.abs(d.x) > 0.5
      ? new THREE.Vector3(0, 0, 1)
      : new THREE.Vector3(1, 0, 0);

  const stone = mats().stone;
  const mid = mats().stoneMid;
  const dark = mats().stoneDark;
  const axleQ = new THREE.Quaternion().setFromUnitVectors(UP, spin);
  // 樋の外面 0.35 のすぐ外側に置く。車輪はセル境界を 0.02 だけはみ出すが、
  // 水車が水路の側面に張り出すのは実物どおりで、frustum の padding 内に収まる。
  const wheelOff = 0.44;
  const wheelThick = 0.16;

  // 軸受け（静止部）：受け台 ＋ 軸
  addGeo(b, boxGeo(0.22, 0.2, 0.22), mid, p.clone().addScaledVector(spin, 0.32));
  addGeo(b, cylinderGeo(0.05, 0.5, 6), dark, p.clone().addScaledVector(spin, 0.25), axleQ);

  // 車輪（回転部）。ローカル Z が回転軸。
  const wheel = new THREE.Group();
  wheel.name = 'wheel';
  wheel.userData.spinAxis = 'z';
  wheel.position.copy(p).addScaledVector(spin, wheelOff);
  wheel.quaternion.setFromUnitVectors(FORWARD, spin);
  const wb = newBuild(wheel);

  const hubQ = new THREE.Quaternion().setFromUnitVectors(UP, FORWARD);
  addGeo(wb, cylinderGeo(0.13, wheelThick, 8), dark, new THREE.Vector3(), hubQ);

  const bladeLen = WHEEL_R - 0.13;
  const blade = boxGeo(bladeLen, 0.055, wheelThick);
  const tips: THREE.Vector3[] = [];
  for (let i = 0; i < 8; i++) {
    const th = (i * Math.PI) / 4;
    const q = new THREE.Quaternion().setFromAxisAngle(FORWARD, th);
    const r = 0.13 + bladeLen / 2;
    addGeo(wb, blade, stone, new THREE.Vector3(Math.cos(th) * r, Math.sin(th) * r, 0), q);
    tips.push(new THREE.Vector3(Math.cos(th) * WHEEL_R, Math.sin(th) * WHEEL_R, 0));
  }
  // 羽根の先を結ぶ外周線。面を足さずに「輪」を見せる（版画の描き方）。
  // 手前側の1周だけで十分に輪に見えるので、裏側は引かない（線分は1本 6 三角形かかる）。
  for (let i = 0; i < 8; i++) {
    pushSeg(
      wb,
      tips[i].clone().setZ(wheelThick / 2),
      tips[(i + 1) % 8].clone().setZ(wheelThick / 2),
    );
  }

  flushInk(wb);
  b.root.add(wheel);
  wheelRegistry.set(piece.id, wheel);
}

/** 装飾の柱：段重ねの柱础・柱頭 ＋ わずかに絞った柱身。 */
/**
 * レバー。台座と、斜めに立った柄。
 *
 * **入っているか切れているかが一目で分かること**が最優先。
 * プレイヤーは「どちらの状態か」を覚えながら視点を探すので、
 * ここが読めないと二軸の探索が成立しない。倒れる向きで状態を示す。
 */
function buildLever(b: Build, def: LeverDef, on: boolean): void {
  const p = toVector3(def.pos);
  const stone = mats().stone;
  const mid = mats().stoneMid;
  const water = mats().water;

  // 台座
  addGeo(b, boxGeo(0.7, 0.18, 0.7), mid, new THREE.Vector3(p.x, p.y - 0.41, p.z));
  addGeo(b, boxGeo(0.5, 0.1, 0.5), stone, new THREE.Vector3(p.x, p.y - 0.27, p.z));
  // 軸受け
  addGeo(b, boxGeo(0.16, 0.3, 0.16), mid, new THREE.Vector3(p.x, p.y - 0.07, p.z));

  // 柄。入れると +x 側へ、切ると -x 側へ倒れる。
  // **ジオメトリは共有キャッシュなので回転で破壊しない。** 姿勢は quaternion で渡す。
  const lean = on ? 1 : -1;
  const tilt = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 0, 1),
    (-lean * Math.PI) / 5,
  );
  addGeo(
    b,
    boxGeo(0.1, 0.62, 0.1),
    stone,
    new THREE.Vector3(p.x + lean * 0.17, p.y + 0.24, p.z),
    tilt,
  );
  // 握り。唯一の彩度を少しだけ乗せて「触れる場所」だと分かるようにする。
  addGeo(
    b,
    boxGeo(0.2, 0.12, 0.2),
    water,
    new THREE.Vector3(p.x + lean * 0.34, p.y + 0.5, p.z),
  );
}

/**
 * 回る足場の台。上に載った水路ごと、鉛直軸のまわりに回る円盤。
 *
 * **回ることが一目で分かること。** 円盤だけでは柱の頭と見分けがつかないので、
 * 縁に一段濃い輪を回し、上面に刻み（向きの印）を入れる。刻みはレバーを倒すと
 * 回った向きを指す——水路の向きが変わったことを、水路以外でも読めるようにする。
 * レバーの握りと同じく、刻みにだけ水の色を乗せて「仕掛け」だと分からせる。
 */
function buildTurntable(b: Build, def: TurntableDef, on: boolean): void {
  const p = toVector3(def.pos);
  const stone = mats().stone;
  const mid = mats().stoneMid;
  const dark = mats().stoneDark;
  const water = mats().water;
  const r = def.radius + 0.42;
  const top = p.y + 0.5;

  // 軸。マスの中を貫く太い円柱。
  addGeo(b, cylinderGeo(0.32, 0.75, 12), mid, new THREE.Vector3(p.x, p.y - 0.12, p.z));
  // 縁の輪（一段濃く、わずかに大きい）と、その上の盤面。
  addGeo(b, cylinderGeo(r + 0.06, 0.1, 32), dark, new THREE.Vector3(p.x, top - 0.2, p.z));
  addGeo(b, cylinderGeo(r, 0.14, 32), stone, new THREE.Vector3(p.x, top - 0.08, p.z));

  // 刻み。倒す前は +x を指し、倒すと turn の分だけ回る（上から見て反時計回り）。
  const quarters = on && def.turn !== undefined ? def.turn.quarters : 0;
  const q = new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(0, 1, 0),
    (-quarters * Math.PI) / 2,
  );
  const tip = new THREE.Vector3(r * 0.62, 0, 0).applyQuaternion(q);
  addGeo(
    b,
    boxGeo(r * 0.7, 0.04, 0.12),
    water,
    new THREE.Vector3(p.x + tip.x / 2 + tip.x * 0.15, top + 0.01, p.z + tip.z / 2 + tip.z * 0.15),
    q,
  );
}

function buildPillar(b: Build, def: PillarDef): void {
  const p = toVector3(def.pos);
  const h = def.height;
  const bottom = p.y - 0.5;
  const top = bottom + h;
  const stone = mats().stone;
  const mid = mats().stoneMid;
  const dark = mats().stoneDark;

  /** 柱础・柱頭の3段モールディング。`dir` は積む向き。 */
  const molding = (base: number, dir: 1 | -1): number => {
    const tiers: readonly (readonly [number, number, THREE.Material])[] = [
      [1.0, 0.08, mid],
      [0.96, 0.06, stone],
      [0.92, 0.05, mid],
    ];
    let y = base;
    for (const [w, th, m] of tiers) {
      addGeo(b, boxGeo(w, th, w), m, new THREE.Vector3(p.x, y + (dir * th) / 2, p.z));
      y += dir * th;
    }
    return Math.abs(y - base);
  };

  const baseH = molding(bottom, 1);
  molding(top, -1);

  // 柱身。マス割りより少し粗い単位でドラムに分け、上へ行くほどわずかに絞る（エンタシス風）。
  const shaftBottom = bottom + baseH;
  const shaftH = top - baseH - shaftBottom;
  if (shaftH > 0.02) {
    const drums = Math.max(1, Math.round(shaftH / 1.2));
    const drumH = shaftH / drums;
    const bandH = Math.min(0.06, drumH * 0.2);
    for (let i = 0; i < drums; i++) {
      const w = Math.max(0.74, PILLAR_W - 0.03 * i);
      const y0 = shaftBottom + drumH * i;
      const solidH = drumH - (i === drums - 1 ? 0 : bandH);
      addGeo(b, boxGeo(w, solidH, w), dark, new THREE.Vector3(p.x, y0 + solidH / 2, p.z));
      if (i < drums - 1) {
        addGeo(
          b,
          boxGeo(w + 0.04, bandH, w + 0.04),
          mid,
          new THREE.Vector3(p.x, y0 + solidH + bandH / 2, p.z),
        );
      }
      // ドラムごとに縦目地を1本ずつ（段で位置をずらして芋目地を避ける）
      const off = i % 2 === 0 ? 0.16 : -0.16;
      const r = w / 2 + INK_LIFT;
      for (const [dx, dz] of [
        [r, off],
        [-r, off],
        [off, r],
        [off, -r],
      ]) {
        pushSeg(
          b,
          new THREE.Vector3(p.x + dx, y0 + solidH * 0.06, p.z + dz),
          new THREE.Vector3(p.x + dx, y0 + solidH * 0.94, p.z + dz),
        );
      }
    }
  }
}

// =============================================================================
// 水チューブの経路
// =============================================================================

function findPort(piece: Piece, id: PortId): Port | undefined {
  return piece.ports.find((port) => port.id === id);
}

/**
 * セグメントの水が通る折れ線。`corner` だけセル中心を経由する 3 点になる。
 * 折れ線の総長は必ず `Segment.cells` と一致する（MODULE-CONTRACTS §2.2 の展開規則より）。
 */
function waterPath(piece: Piece, seg: Segment): THREE.Vector3[] {
  const from = findPort(piece, seg.fromPort);
  const to = findPort(piece, seg.toPort);
  if (from === undefined || to === undefined) return [];
  if (piece.def.type === 'corner') {
    return [toVector3(from.pos), toVector3(piece.def.pos), toVector3(to.pos)];
  }
  return [toVector3(from.pos), toVector3(to.pos)];
}

// =============================================================================
// 公開 API（MODULE-CONTRACTS §7）
// =============================================================================

/**
 * ピース1つぶんのメッシュを作る。
 * - 戻り値の `Object3D.position` は原点のまま。ワールド座標は子の transform が持つ。
 * - 副作用: 生成した WaterMesh / 車輪 / ポート位置をモジュール内レジストリに登録する。
 * - `Object3D.name` に `piece.id` を入れる。
 */
/**
 * @param leverOn レバーの状態。レバーの柄の向きにだけ影響する
 *                （動くピースの座標は `core/pieces.ts` が既に反映済み）。
 */
export function buildPieceMesh(piece: Piece, leverOn = false): THREE.Object3D {
  const group = new THREE.Group();
  group.name = piece.id;
  const b = newBuild(group);

  const def = piece.def;
  switch (def.type) {
    case 'source':
      buildSource(b, def);
      break;
    case 'goal':
      buildGoal(b, def);
      break;
    // 涸れ井戸。形は同じで**乾いて見える**ようにする。
    // 別の形にすると、水が溜まる場所だと分からなくなる——
    // 「入ってはいけない受け口」であることが伝わる必要がある。
    case 'drain':
      buildGoal(b, { ...def, type: 'goal' }, true);
      break;
    case 'straight':
      buildStraight(b, def);
      break;
    case 'corner':
      buildCorner(b, def);
      break;
    case 'fall':
      buildFall(b, def);
      break;
    case 'waterwheel':
      buildWaterwheel(b, piece, def);
      break;
    case 'pillar':
      buildPillar(b, def);
      break;
    case 'lever':
      buildLever(b, def, leverOn);
      break;
    case 'turntable':
      buildTurntable(b, def, leverOn);
      break;
    default: {
      const never: never = def;
      throw new Error(`unknown piece type: ${JSON.stringify(never)}`);
    }
  }
  flushInk(b);

  for (const port of piece.ports) {
    anchorRegistry.set(port.id, toVector3(port.pos));
  }

  const width = def.type === 'fall' ? FALL_WATER_W : WATER_W;
  for (const seg of piece.segments) {
    const path = waterPath(piece, seg);
    if (path.length < 2) continue;
    const previous = waterRegistry.get(seg.id);
    if (previous !== undefined) previous.disposeParts();
    waterRegistry.set(seg.id, new WaterTube(group, seg.id, path, width));
  }

  return group;
}

/** 登録済みの水チューブ。キーは SegmentId。 */
export function getWaterMeshes(): ReadonlyMap<SegmentId, WaterMesh> {
  return waterRegistry;
}

/** waterwheel の回転する車輪部分。キーは PieceId。ローカル Z 軸まわりに回すこと。 */
export function getWheelMeshes(): ReadonlyMap<string, THREE.Object3D> {
  return wheelRegistry;
}

/** 行き止まりのこぼれ演出の発生位置（= Port.pos）。キーは PortId。 */
export function getPortAnchors(): ReadonlyMap<PortId, THREE.Vector3> {
  return anchorRegistry;
}

/** レベル切替時に呼ぶ。レジストリを空にし、生成した geometry を dispose する。 */
export function clearMeshRegistry(): void {
  for (const tube of waterRegistry.values()) tube.disposeParts();
  waterRegistry.clear();
  wheelRegistry.clear();
  anchorRegistry.clear();
  for (const geom of inkGeometries) geom.dispose();
  inkGeometries.length = 0;
  for (const geom of waterEdgeCache.values()) geom.dispose();
  waterEdgeCache.clear();
  for (const geom of geometryCache.values()) geom.dispose();
  geometryCache.clear();
  edgeCache.clear();
}

/** `clearMeshRegistry()` の別名。レベル切替時のリーク防止用。 */
export function disposePieceMeshes(): void {
  clearMeshRegistry();
}
